import { useCallback, useState } from "react";
import {
  View,
  Text,
  Pressable,
  Image,
  Modal,
  type ImageSourcePropType,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  X,
  Info,
  Heart,
  Beaker,
  History,
  Upload,
  FileText,
  CalendarDays,
  ChevronRight,
  type LucideIcon,
} from "lucide-react-native";
import Svg, { Circle, Line, Path, Text as SvgText } from "react-native-svg";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  runOnJS,
} from "react-native-reanimated";
import {
  IconButton,
  useScrollHaptics,
  springs,
  SectionTitle,
  MoonStatBlock,
} from "@louise/moon-ui";
import { EMBER } from "../../onboarding/theme";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import FactSheetHeader, {
  FactSheetBadge,
  type FactSheetHeaderBadge,
} from "./FactSheetHeader";
import FactSheetContextCard from "./FactSheetContextCard";
import { getInterpretationContextRows } from "./FactSheetInterpretationContext";
import StatusBadge from "./StatusBadge";
import { DocumentRow } from "../screens/documents/DocumentRow";
import { useTranslations } from "@src/packages/i18n";
import type { BiomarkerTestingMethod } from "@src/packages/content";
import type { ResultInterpretationContext } from "@src/packages/test-results/interpretation-context";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
// ── Types ──

export type MetricHistoryPoint = {
  day: string;
  date: string;
  value: string;
};

export type MetricChartPoint = {
  label: string;
  sublabel?: string;
  value: number;
};

export type MetricChartSeries = {
  label: string;
  color?: string;
  cycleLink?: MetricCycleLink;
  points: MetricChartPoint[];
};

export type MetricRelatedDocument = {
  id: string;
  title: string;
  subtitle?: string;
  date?: string;
};

export type MetricTestingMethod = BiomarkerTestingMethod;

export type MetricCycleLink = {
  label: string;
  cycleId?: string | null;
  isPast?: boolean;
};

export type ResponseMetric = {
  label: string;
  current?: string;
  unit?: string;
  previous?: string;
  trend: "up" | "down" | "stable";
  context?: string;
  contextGood?: boolean;
  description: string;
  whyItMatters: string;
  normalRange: string;
  history: MetricHistoryPoint[];
  valueMode?: "cycle" | "lifetime";
  chartSeries?: MetricChartSeries[];
  imageSource?: ImageSourcePropType;
  relatedDocuments?: MetricRelatedDocument[];
  cycleLink?: MetricCycleLink;
  testingMethods?: MetricTestingMethod[];
  interpretationContext?: ResultInterpretationContext;
};

// ── MetricCard — the tappable tile ──

export function MetricCard({
  metric,
  onPress,
}: {
  metric: ResponseMetric;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("MetricCard");
  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(metric.label), "metric_card");
        onPress();
      }}
      style={{
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.02)",
        borderRadius: 12,
        padding: 12,
      }}
    >
      <MoonStatBlock
        label={metric.label}
        value={metric.current ?? "Not tracked"}
        unit={metric.unit ?? ""}
        layout="label-first"
        trend={metric.trend === "stable" ? "flat" : metric.trend}
      />
    </Pressable>
  );
}

function MetricInfoSection({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: string;
}) {
  return (
    <View style={{ gap: 8 }}>
      <SectionTitle icon={Icon}>{title}</SectionTitle>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          color: EMBER.colors.textMid,
          lineHeight: 21,
        }}
      >
        {children}
      </Text>
    </View>
  );
}

function toChartSeries(metric: ResponseMetric): MetricChartSeries[] {
  if (metric.chartSeries?.length) return metric.chartSeries;

  const points = metric.history
    .map((point): MetricChartPoint | null => {
      const value = Number.parseFloat(point.value);
      if (!Number.isFinite(value)) return null;
      return {
        label: point.day,
        sublabel: point.date,
        value,
      };
    })
    .filter((point): point is MetricChartPoint => point !== null);

  return points.length
    ? [
        {
          label: metric.cycleLink?.label ?? metric.label,
          cycleLink: metric.cycleLink,
          points,
        },
      ]
    : [];
}

function makePath(points: { x: number; y: number }[]) {
  return points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
    .join(" ");
}

function MetricSeriesChart({
  series,
  minValue,
  maxValue,
  stacked,
  onOpenCycle,
}: {
  series: MetricChartSeries;
  minValue: number;
  maxValue: number;
  stacked: boolean;
  onOpenCycle?: (cycleLink: MetricCycleLink) => void;
}) {
  const capturePress = usePressCapture("MetricSeriesChart");
  const width = 300;
  const height = stacked ? 112 : 188;
  const left = 22;
  const right = 20;
  const top = 24;
  const bottom = 28;
  const color = series.color ?? EMBER.colors.warm;
  const valueSpan = Math.max(1, maxValue - minValue);
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const positioned = series.points.map((point, index) => {
    const x =
      series.points.length === 1
        ? left + plotWidth / 2
        : left + (plotWidth * index) / (series.points.length - 1);
    const y =
      top + plotHeight - ((point.value - minValue) / valueSpan) * plotHeight;
    return { ...point, x, y };
  });
  const canOpenCycle = Boolean(
    series.cycleLink &&
    onOpenCycle &&
    (!series.cycleLink.isPast || series.cycleLink.cycleId),
  );

  return (
    <View
      style={{
        paddingHorizontal: 12,
        paddingTop: 12,
        paddingBottom: 8,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.055)",
        backgroundColor: "rgba(255,255,255,0.42)",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          marginBottom: 4,
        }}
      >
        {series.cycleLink ? (
          canOpenCycle ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`Open ${series.cycleLink.label}`}
              onPress={() => {
                capturePress(
                  pressLabel(series.cycleLink!.label),
                  "open_cycle_from_metric",
                );
                onOpenCycle?.(series.cycleLink!);
              }}
              hitSlop={6}
              style={({ pressed }) => ({
                opacity: pressed ? 0.72 : 1,
              })}
            >
              <StatusBadge
                label={series.cycleLink.label}
                icon={CalendarDays}
                tone="neutral"
                shape="pill"
              />
            </Pressable>
          ) : (
            <StatusBadge
              label={series.cycleLink.label}
              icon={CalendarDays}
              tone="neutral"
              shape="pill"
            />
          )
        ) : (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              fontWeight: "700",
              color: EMBER.colors.textMid,
            }}
          >
            {series.label}
          </Text>
        )}
      </View>
      <Svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
        <Line
          x1={left}
          x2={width - right}
          y1={height - bottom}
          y2={height - bottom}
          stroke="rgba(0,0,0,0.08)"
          strokeWidth={1}
        />
        <Line
          x1={left}
          x2={width - right}
          y1={top}
          y2={top}
          stroke="rgba(0,0,0,0.045)"
          strokeWidth={1}
        />
        {positioned.length > 1 ? (
          <Path
            d={makePath(positioned)}
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : null}
        {positioned.map((point, index) => (
          <SvgText
            key={`value-${index}`}
            x={point.x}
            y={Math.max(12, point.y - 10)}
            fill={EMBER.colors.text}
            fontSize={11}
            fontWeight="700"
            textAnchor="middle"
          >
            {point.value}
          </SvgText>
        ))}
        {positioned.map((point, index) => (
          <Circle
            key={`dot-${index}`}
            cx={point.x}
            cy={point.y}
            r={4.5}
            fill="#fff"
            stroke={color}
            strokeWidth={2}
          />
        ))}
        {positioned.map((point, index) => (
          <SvgText
            key={`label-${index}`}
            x={point.x}
            y={height - 10}
            fill={EMBER.colors.textFaint}
            fontSize={10}
            textAnchor="middle"
          >
            {point.label}
          </SvgText>
        ))}
      </Svg>
    </View>
  );
}

function MetricValuesChart({
  metric,
  onOpenCycle,
}: {
  metric: ResponseMetric;
  onOpenCycle?: (cycleLink: MetricCycleLink) => void;
}) {
  const series = toChartSeries(metric);
  if (!series.length) return null;

  const values = series.flatMap((item) =>
    item.points.map((point) => point.value),
  );
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const padding = Math.max(1, (rawMax - rawMin) * 0.12);
  const minValue = Math.max(0, rawMin - padding);
  const maxValue = rawMax + padding;
  const stacked = metric.valueMode === "cycle" && series.length > 1;

  return (
    <View style={{ marginTop: 10, gap: 10 }}>
      {stacked ? (
        series.map((item) => (
          <MetricSeriesChart
            key={item.label}
            series={item}
            minValue={minValue}
            maxValue={maxValue}
            stacked
            onOpenCycle={onOpenCycle}
          />
        ))
      ) : (
        <MetricSeriesChart
          series={series[0]!}
          minValue={minValue}
          maxValue={maxValue}
          stacked={false}
          onOpenCycle={onOpenCycle}
        />
      )}
    </View>
  );
}

function MetricEmptyValues({
  onUploadResults,
}: {
  onUploadResults?: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("MetricEmptyValues");
  return (
    <View
      style={{
        marginTop: 10,
        padding: 16,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.055)",
        backgroundColor: "rgba(255,255,255,0.42)",
        gap: 10,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          fontWeight: "700",
          color: EMBER.colors.textMid,
          lineHeight: 18,
        }}
      >
        {t("care.cycle_detail.detail.not_tracked_title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          color: EMBER.colors.textLight,
          lineHeight: 19,
        }}
      >
        {t("care.cycle_detail.detail.not_tracked_body")}
      </Text>
      {onUploadResults ? (
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(t("care.cycle_detail.detail.upload_results")),
              "care.cycle_detail.detail.upload_results",
            );
            onUploadResults();
          }}
          style={({ pressed }) => ({
            alignSelf: "flex-start",
            flexDirection: "row",
            alignItems: "center",
            gap: 7,
            borderRadius: 8,
            paddingHorizontal: 12,
            paddingVertical: 8,
            backgroundColor: pressed
              ? `${EMBER.colors.warm}CC`
              : EMBER.colors.warm,
          })}
        >
          <Upload size={13} color="#fff" strokeWidth={2} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              fontWeight: "700",
              color: "#fff",
            }}
          >
            {t("care.cycle_detail.detail.upload_results")}
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
}

function MetricRelatedDocuments({
  documents,
  onUploadResults,
}: {
  documents?: MetricRelatedDocument[];
  onUploadResults?: () => void;
}) {
  const t = useTranslations();
  if (!documents?.length && !onUploadResults) return null;

  return (
    <View style={{ marginTop: 24 }}>
      <SectionTitle icon={FileText}>
        {t("care.cycle_detail.detail.related_documents")}
      </SectionTitle>
      <View style={{ marginTop: 10, gap: 8 }}>
        {documents?.map((document) => (
          <DocumentRow
            key={document.id}
            title={document.title}
            subtitle={document.subtitle}
            dateLabel={document.date}
            icon={
              <FileText
                size={18}
                color={EMBER.colors.textLight}
                strokeWidth={1.5}
              />
            }
            showChevron={false}
          />
        ))}
        {onUploadResults ? (
          <DocumentRow
            title={t("care.cycle_detail.detail.upload_results")}
            icon={
              <Upload
                size={18}
                color={EMBER.colors.textLight}
                strokeWidth={1.5}
              />
            }
            onPress={onUploadResults}
            showChevron={false}
          />
        ) : null}
      </View>
    </View>
  );
}

const SAMPLE_TYPE_LABELS: Record<string, string> = {
  blood: "Blood",
  saliva: "Saliva",
  urine: "Urine",
  semen: "Semen",
  ultrasound: "Ultrasound",
  imaging: "Imaging",
  genetic: "Genetic",
  swab: "Swab",
  tissue: "Tissue",
};

function MetricTestingMethodContent({
  method,
  showChevron,
}: {
  method: MetricTestingMethod;
  showChevron: boolean;
}) {
  return (
    <>
      <View
        style={{
          width: 34,
          height: 34,
          borderRadius: 17,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0,0,0,0.045)",
        }}
      >
        <Beaker size={16} color={EMBER.colors.textMid} />
      </View>
      <View style={{ flex: 1, gap: 6 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            fontWeight: "700",
            color: EMBER.colors.textMid,
            lineHeight: 18,
          }}
        >
          {method.title}
        </Text>
        {method.subtitle ? (
          <Text
            className="font-dm-sans"
            numberOfLines={2}
            style={{
              fontSize: 12,
              color: EMBER.colors.textLight,
              lineHeight: 17,
            }}
          >
            {method.subtitle}
          </Text>
        ) : null}
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
          {method.sampleTypes.map((sampleType) => (
            <FactSheetBadge
              key={sampleType}
              label={SAMPLE_TYPE_LABELS[sampleType] ?? sampleType}
            />
          ))}
          <FactSheetBadge label={method.collectionLabel} />
          {method.timing ? <FactSheetBadge label={method.timing} /> : null}
        </View>
      </View>
      {showChevron ? (
        <ChevronRight size={16} color={EMBER.colors.textFaint} />
      ) : null}
    </>
  );
}

function MetricTestingMethods({
  methods,
  onMethodPress,
}: {
  methods?: MetricTestingMethod[];
  onMethodPress?: (method: MetricTestingMethod) => void;
}) {
  const capturePress = usePressCapture("MetricTestingMethods");
  if (!methods?.length) return null;

  const rowBaseStyle = {
    flexDirection: "row" as const,
    alignItems: "center" as const,
    gap: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
  };

  return (
    <View style={{ marginTop: 24 }}>
      <SectionTitle icon={Beaker}>How to test this</SectionTitle>
      <View style={{ marginTop: 10, gap: 8 }}>
        {methods.map((method) =>
          onMethodPress ? (
            <Pressable
              key={method.testId}
              onPress={() => {
                capturePress(pressLabel(method.title), "metric_test_method");
                onMethodPress(method);
              }}
              style={({ pressed }) => ({
                ...rowBaseStyle,
                backgroundColor: pressed
                  ? "rgba(0,0,0,0.04)"
                  : "rgba(255,255,255,0.34)",
              })}
            >
              <MetricTestingMethodContent method={method} showChevron />
            </Pressable>
          ) : (
            <View
              key={method.testId}
              style={{
                ...rowBaseStyle,
                backgroundColor: "rgba(255,255,255,0.34)",
              }}
            >
              <MetricTestingMethodContent method={method} showChevron={false} />
            </View>
          ),
        )}
      </View>
    </View>
  );
}

// ── MetricDetailSheet — the full detail modal ──

function MetricDetailContent({
  metric,
  onClose,
  onUploadResults,
  onOpenCycle,
  onOpenTest,
}: {
  metric: ResponseMetric;
  onClose: () => void;
  onUploadResults?: () => void;
  onOpenCycle?: (cycleLink: MetricCycleLink) => void;
  onOpenTest?: (testId: string) => void;
}) {
  const insets = useSafeAreaInsets();
  const t = useTranslations();
  const scale = useSharedValue(0.92);
  const opacity = useSharedValue(0);
  const onScrollHaptics = useScrollHaptics();
  const hasValues = metric.history.length > 0 || !!metric.chartSeries?.length;
  const valueLabel = metric.current
    ? `${metric.current}${metric.unit ? ` ${metric.unit}` : ""}`
    : t("care.cycle_detail.detail.not_tracked_badge");
  const valuesTitle =
    metric.valueMode === "lifetime"
      ? t("care.cycle_detail.detail.values_over_time")
      : metric.valueMode === "cycle" || metric.cycleLink
        ? t("care.cycle_detail.detail.values_by_cycle")
        : t("care.cycle_detail.detail.your_values_this_cycle");
  useState(() => {
    scale.value = withSpring(1, springs.sheet);
    opacity.value = withTiming(1, { duration: 250 });
  });

  const animatedStyle = useAnimatedStyle(() => ({
    flex: 1,
    overflow: "hidden" as const,
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  const handleClose = useCallback(() => {
    scale.value = withSpring(0.92, springs.sheet);
    opacity.value = withTiming(0, { duration: 200 }, () => {
      runOnJS(onClose)();
    });
  }, [scale, opacity, onClose]);
  const handleCyclePress = useCallback(
    (cycleLink: MetricCycleLink) => {
      handleClose();
      setTimeout(() => onOpenCycle?.(cycleLink), 220);
    },
    [handleClose, onOpenCycle],
  );
  const handleTestingMethodPress = useCallback(
    (method: MetricTestingMethod) => {
      handleClose();
      setTimeout(() => onOpenTest?.(method.testId), 220);
    },
    [handleClose, onOpenTest],
  );

  const badges: FactSheetHeaderBadge[] = [
    { label: valueLabel, color: EMBER.colors.text },
  ];
  if (metric.previous) {
    badges.push({
      label: t("care.cycle_detail.detail.previous_value", {
        params: { value: metric.previous, unit: metric.unit ?? "" },
      }),
    });
  }

  const contextRows = metric.interpretationContext
    ? getInterpretationContextRows(metric.interpretationContext, t)
    : [];

  return (
    <View style={{ flex: 1, overflow: "hidden" }}>
      <Image
        source={require("@louise/moon-ui/assets/images/onboarding-bg.jpg")}
        style={{
          position: "absolute",
          width: "115%",
          height: "115%",
          left: "-7.5%",
          top: "-7.5%",
        }}
        resizeMode="cover"
      />
      <Animated.View style={animatedStyle}>
        <View style={{ flex: 1, paddingTop: insets.top }}>
          <Animated.ScrollView
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingTop: SCREEN_PADDING_TOP,
              paddingBottom: 100,
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
            }}
            showsVerticalScrollIndicator={false}
            onScroll={onScrollHaptics}
            scrollEventThrottle={16}
          >
            <View
              style={{
                ...GLASS_CARD_STYLE,
                paddingHorizontal: 24,
                paddingTop: 24,
                paddingBottom: 32,
              }}
            >
              {/* Close */}
              <View
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  zIndex: 2,
                }}
              >
                <IconButton
                  onPress={handleClose}
                  hitSlop={8}
                  size={32}
                  surface="soft"
                  backgroundColor="rgba(0, 0, 0, 0.05)"
                  pressedBackgroundColor="rgba(0, 0, 0, 0.05)"
                  pressedOpacity={1}
                  accessibilityLabel="Close"
                >
                  <X size={16} color={EMBER.colors.textLight} />
                </IconButton>
              </View>

              <FactSheetHeader
                title={metric.label}
                imageSource={metric.imageSource}
                imageSize={118}
                visual={
                  metric.imageSource ? undefined : (
                    <Beaker
                      size={54}
                      color={EMBER.colors.textMid}
                      strokeWidth={1.35}
                    />
                  )
                }
                badges={badges}
              />

              {metric.interpretationContext ? (
                <FactSheetContextCard
                  label={t("care.lab_result.context_title")}
                  meta={t("care.lab_result.context_meta")}
                  rows={contextRows}
                  expandLabel={t("care.lab_result.context_show_details")}
                  collapseLabel={t("care.lab_result.context_hide_details")}
                />
              ) : null}

              {hasValues ? (
                <View style={{ marginTop: 18 }}>
                  <SectionTitle icon={History}>{valuesTitle}</SectionTitle>
                  <MetricValuesChart
                    metric={metric}
                    onOpenCycle={onOpenCycle ? handleCyclePress : undefined}
                  />
                </View>
              ) : (
                <View style={{ marginTop: 18 }}>
                  <SectionTitle icon={History}>
                    {t("care.cycle_detail.detail.values_over_time")}
                  </SectionTitle>
                  <MetricEmptyValues onUploadResults={onUploadResults} />
                </View>
              )}

              <MetricTestingMethods
                methods={metric.testingMethods}
                onMethodPress={
                  onOpenTest ? handleTestingMethodPress : undefined
                }
              />

              <View style={{ marginTop: 24, gap: 24 }}>
                <MetricInfoSection
                  icon={Info}
                  title={t("care.cycle_detail.detail.what_is_it")}
                >
                  {metric.description}
                </MetricInfoSection>
                <MetricInfoSection
                  icon={Heart}
                  title={t("care.cycle_detail.detail.why_it_matters")}
                >
                  {metric.whyItMatters}
                </MetricInfoSection>
                <MetricInfoSection
                  icon={Beaker}
                  title={t("care.cycle_detail.detail.typical_range")}
                >
                  {metric.normalRange}
                </MetricInfoSection>
              </View>

              <MetricRelatedDocuments
                documents={metric.relatedDocuments}
                onUploadResults={onUploadResults}
              />
            </View>
          </Animated.ScrollView>
        </View>
      </Animated.View>
    </View>
  );
}

export default function MetricDetailSheet({
  metric,
  onClose,
  onUploadResults,
  onOpenCycle,
  onOpenTest,
}: {
  metric: ResponseMetric | null;
  onClose: () => void;
  onUploadResults?: () => void;
  onOpenCycle?: (cycleLink: MetricCycleLink) => void;
  onOpenTest?: (testId: string) => void;
}) {
  if (!metric) return null;
  return (
    <Modal visible transparent animationType="none" onRequestClose={onClose}>
      <MetricDetailContent
        metric={metric}
        onClose={onClose}
        onUploadResults={onUploadResults}
        onOpenCycle={onOpenCycle}
        onOpenTest={onOpenTest}
      />
    </Modal>
  );
}
